import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../../../context/ThemeContext";
import { Badge, Image, Button, Modal } from "react-bootstrap";
import {
  X,
  ThumbsUp,
  ZoomIn,
  Repeat2,
  Bookmark,
  Info,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import communityService from "../../../../api/communityService";
import { FacebookCounter, FacebookSelector } from "@charkour/react-reactions";
import CommentsSection from "../comment-section/CommentsSection";
import { useDispatch } from "react-redux";
import {
  activityStart,
  activityEnd,
} from "../../../../redux/slices/trackActivitySlice";
import usePlan from "../../../../hooks/usePlan";
import { getpostdetails } from "../../../../redux/slices/postdetailsSlice";
import "../../styles/Community.scss";
import "../article-detail-modal/ArticleDetailModal.scss";
import {
  linkifyText,
  sanitizeRichContent,
  getVisibleTextLength,
} from "../../utils/communityHelpers";
import parse from "html-react-parser";
import Linkify from "linkify-react";
import PollWidget from "../poll-widget/PollWidget";
import { usePosts } from "../../hooks";
import Carousel from "react-bootstrap/Carousel";
import wowImg from "../../../../assets/images/Community_emojis/wow.png";
import hahaImg from "../../../../assets/images/Community_emojis/haha.png";
import loveImg from "../../../../assets/images/Community_emojis/Heart.png";
import angryImg from "../../../../assets/images/Community_emojis/angry.png";
import sadImg from "../../../../assets/images/Community_emojis/sad.png";
import ImageZoomModal from "../image-zoom-modal";
import UserProfileModal from "../user-profile-modal/UserProfileModal";
import ShareModal from "../share-modal/ShareModal";
import RestrictionModal from "../../../restriction-modals/RestrictionModal";
import { getUser } from "../../../../utils/auth";
import { FiX } from "react-icons/fi";
import { ReactComponent as ChevronRightIcon } from "../../../../assets/images/btn-right-aero.svg";

// Report reasons — same list PostCard and RightSidebar use so the report
// modal stays identical across the feed, sidebar, and post detail view.
const REPORT_REASONS = [
  "Spam",
  "Nudity",
  "Scam",
  "Hate Speech",
  "Harassment",
  "Violence",
  "Misinformation",
  "Terrorism",
  "Illegal Activity",
  "Child Exploitation",
];

/**
 * PostDetailModal
 * Same data / UI / UX as the standalone PostDetails page (pages/notification/
 * postDetails.js), but rendered as an in-place modal so opening a post from the
 * community feed does NOT unmount the feed — preserving scroll position and
 * avoiding a feed refetch.
 *
 * @param {boolean} show           Whether the modal is visible
 * @param {number|string} postId   Post id to load
 * @param {Function} onClose       Called when the modal should close
 * @param {Function} [onReaction]  Optional (postId, reactionType) — used so the
 *                                  feed reflects reaction changes live + handles
 *                                  the API call. Falls back to the local hook.
 * @param {Function} [onCommentCountUpdate] Optional (postId, count) to sync the
 *                                  feed's comment count.
 */
const PostDetailModal = ({
  show,
  postId,
  planName,
  onClose,
  onReaction,
  onCommentCountUpdate,
  onShare,
  onReport,
}) => {
  const dispatch = useDispatch();
  const { resolvedTheme } = useTheme();
  const imageNotAvailableSrc =
    resolvedTheme === "dark"
      ? "data:image/svg+xml;base64," +
        btoa(
          '<svg width="600" height="400" xmlns="http://www.w3.org/2000/svg"><rect width="600" height="400" fill="#1a2830"/><text x="50%" y="50%" font-family="Arial, sans-serif" font-size="18" fill="#9aa7ac" text-anchor="middle" dominant-baseline="middle">Image Not Available</text></svg>',
        )
      : "data:image/svg+xml;base64," +
        btoa(
          '<svg width="600" height="400" xmlns="http://www.w3.org/2000/svg"><rect width="600" height="400" fill="#e5e7eb"/><text x="50%" y="50%" font-family="Arial, sans-serif" font-size="18" fill="#6b7280" text-anchor="middle" dominant-baseline="middle">Image Not Available</text></svg>',
        );

  useEffect(() => {
    if (!show) return;
    dispatch(activityStart({ page_name: "POST_DETAILS", platform: "web" }));
    return () => dispatch(activityEnd({ page_name: "POST_DETAILS" }));
  }, [show, dispatch]);
  const { addOrUpdateReaction } = usePosts();

  const [currentPost, setCurrentPost] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [hoverHideTimer, setHoverHideTimer] = useState(null);
  const [index, setIndex] = useState(0);

  // Image zoom modal state
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);

  // Original post image zoom modal state
  const [showOriginalImageZoom, setShowOriginalImageZoom] = useState(false);
  const [originalZoomImageIndex, setOriginalZoomImageIndex] = useState(0);

  // User Profile Modal state
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [
    showOriginalUserProfileModal,
    setShowOriginalUserProfileModal,
  ] = useState(false);

  // Subscription restriction state
  const [showPaywallSheet, setShowPaywallSheet] = useState(false);

  // Save/bookmark restriction shown when a free user tries to save a post
  const [showSaveRestriction, setShowSaveRestriction] = useState(false);

  // Fallback share modal — used when no parent `onShare` handler is provided
  // (e.g. the standalone /community/:id route). Keeps share working everywhere.
  const [showShareModal, setShowShareModal] = useState(false);

  // Saved / bookmark state — same flow as PostCard.js
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Report flow — mirrors PostCard / RightSidebar: click Info → reasons modal.
  const [reportReason, setReportReason] = useState("");
  const [showReportModal, setShowReportModal] = useState(false);

  // Reactions modal state
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [selectedReactionUser, setSelectedReactionUser] = useState(null);
  const [
    showReactionUserProfileModal,
    setShowReactionUserProfileModal,
  ] = useState(false);

  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isPaid: isProUser } = usePlan();

  // Global user profile view count using localStorage (persists across sessions)
  const getUserProfileViewCount = () => {
    const count = localStorage.getItem("userProfileViewCount");
    return count ? parseInt(count, 10) : 0;
  };

  const incrementUserProfileViewCount = () => {
    const currentCount = getUserProfileViewCount();
    localStorage.setItem("userProfileViewCount", (currentCount + 1).toString());
  };

  // State for "See more" functionality
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOriginalPostExpanded, setIsOriginalPostExpanded] = useState(false);

  const getFirstLinkFromContent = (content = "") => {
    const div = document.createElement("div");
    div.innerHTML = content;
    const plainText = div.textContent || div.innerText || "";
    const urlRegex = /(https?:\/\/[^\s]+)/i;
    const match = plainText.match(urlRegex);
    return match ? match[0] : null;
  };
  const contentLink = useMemo(
    () => getFirstLinkFromContent(currentPost?.content),
    [currentPost?.content],
  );
  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  // Fetch the post whenever the modal opens (or the target post changes).
  // Clear first so a previously-open post doesn't flash while loading.
  useEffect(() => {
    if (!show || !postId) return;
    let cancelled = false;
    setCurrentPost(null);
    setIsExpanded(false);
    setIsOriginalPostExpanded(false);
    setIndex(0);
    (async () => {
      try {
        const result = await dispatch(getpostdetails(postId));
        if (cancelled) return;
        setCurrentPost(result?.payload?.data?.post || null);
      } catch (error) {
        console.error("Error fetching post details:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, postId, dispatch]);

  // Seed isSaved: first from the fetched post payload (if it carries
  // is_saved), then confirm with /frontend/post-saves/check.
  useEffect(() => {
    if (!currentPost?.id) return;

    if (typeof currentPost?.is_saved !== "undefined") {
      setIsSaved(Boolean(currentPost.is_saved));
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await communityService.checkPostSaved(currentPost.id);
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data ?? {};
        const flag =
          payload.is_saved ??
          payload.isSaved ??
          payload.saved ??
          payload.status ??
          null;
        if (flag !== null && flag !== undefined) {
          setIsSaved(Boolean(Number(flag)));
        }
      } catch (err) {
        console.error("Error checking post saved status:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentPost?.id]);

  // Toggle save/bookmark — same handler as PostCard.js
  const handleSaveClick = async () => {
    if (!currentPost?.id || saveLoading) return;

    // Free users cannot save posts — show the upgrade modal. Un-saving an
    // already-saved post is still allowed for everyone.
    if (!isProUser && !isSaved) {
      setShowSaveRestriction(true);
      return;
    }

    const previous = isSaved;
    const next = !previous;
    setIsSaved(next); // optimistic
    setSaveLoading(true);

    try {
      const response = await communityService.togglePostSave(currentPost.id);
      if (response?.data?.success) {
        toast.success(
          response?.data?.message ||
            (next ? "Post saved" : "Removed from saved"),
        );
      } else {
        setIsSaved(previous);
        toast.error(response?.data?.message || "Failed to update save");
      }
    } catch (err) {
      console.error("Error toggling post save:", err);
      setIsSaved(previous);
      toast.error(err?.response?.data?.message || "Failed to update save");
    } finally {
      setSaveLoading(false);
    }
  };

  // Share — hand the current post to the feed handler, which opens the shared
  // share modal (same one PostCard uses). Falls back to a no-op if absent.
  const handleShareClick = () => {
    if (!currentPost) return;
    // Prefer the feed-level handler (opens Community's share modal). When this
    // modal is rendered standalone (e.g. /community/:id route) there is no
    // parent handler, so fall back to our own ShareModal.
    if (onShare) {
      onShare(currentPost);
    } else {
      setShowShareModal(true);
    }
  };

  // Report — open the reasons modal; on submit route through the feed handler
  // (API call), falling back to a direct service call. Mirrors RightSidebar.
  const closeReportModal = () => {
    setShowReportModal(false);
    setReportReason("");
  };
  const submitReport = async () => {
    if (!currentPost?.id || !reportReason) {
      toast.error("Please select a reason for reporting");
      return;
    }
    try {
      if (onReport) {
        await onReport(currentPost.id, reportReason);
      } else {
        await communityService.reportPost(currentPost.id, reportReason);
        toast.success("Post reported successfully");
      }
    } catch (err) {
      console.error("Post detail report failed:", err);
    } finally {
      closeReportModal();
    }
  };

  // Route the API call through the feed handler when provided so the feed
  // card updates live; otherwise use the local hook (API only).
  const persistReaction = (id, type) =>
    onReaction ? onReaction(id, type) : addOrUpdateReaction(id, type);

  const handleLike = async () => {
    const currentReaction = currentPost.user_reaction;
    const isCurrentlyLiked = currentReaction === "like";

    setCurrentPost((prev) => {
      if (!prev) return prev;

      const reactions = { ...prev.reactions };
      let likesCount = Object.values(reactions).reduce((a, b) => a + b, 0);

      if (currentReaction && currentReaction !== "like") {
        reactions[currentReaction] = Math.max(
          0,
          (reactions[currentReaction] || 0) - 1,
        );
        likesCount = likesCount - 1;
      }

      if (isCurrentlyLiked) {
        reactions.like = Math.max(0, (reactions.like || 0) - 1);
        likesCount = likesCount - 1;
        return {
          ...prev,
          user_reaction: null,
          reactions,
          likes_count: likesCount,
        };
      } else {
        reactions.like = (reactions.like || 0) + 1;
        likesCount = likesCount + 1;
        return {
          ...prev,
          user_reaction: "like",
          reactions,
          likes_count: likesCount,
        };
      }
    });

    try {
      await persistReaction(currentPost.id, "like");
    } catch (error) {
      console.error("Reaction update failed:", error);
    }
  };

  const handleReactionPick = async (newReaction) => {
    const currentReaction = currentPost.user_reaction;

    setCurrentPost((prev) => {
      if (!prev) return prev;

      const reactions = { ...prev.reactions };
      let likesCount = Object.values(reactions).reduce((a, b) => a + b, 0);

      if (currentReaction) {
        reactions[currentReaction] = Math.max(
          0,
          (reactions[currentReaction] || 0) - 1,
        );
        likesCount = likesCount - 1;
      }

      reactions[newReaction] = (reactions[newReaction] || 0) + 1;
      likesCount = likesCount + 1;

      return {
        ...prev,
        user_reaction: newReaction,
        reactions,
        likes_count: likesCount,
      };
    });

    setShowReactionPicker(false);

    try {
      await persistReaction(currentPost.id, newReaction);
    } catch (error) {
      console.error("Reaction update failed:", error);
    }
  };

  const handleReactionsEnter = () => {
    if (hoverHideTimer) clearTimeout(hoverHideTimer);
    setShowReactionPicker(true);
  };
  const handleReactionsLeave = () => {
    const t = setTimeout(() => setShowReactionPicker(false), 250);
    setHoverHideTimer(t);
  };

  /**
   * Handle main post user profile click with subscription restriction
   */
  const handleUserProfileClick = (e) => {
    e.stopPropagation();

    const currentUser = getUser();
    const isOwnPost = currentUser?.id === currentPost?.user_id;
    const isDeletedUserLocal =
      !currentPost?.user_id && currentPost?.is_post_anonymously !== 1;

    if (
      currentPost?.is_post_anonymously === 1 ||
      isOwnPost ||
      isDeletedUserLocal
    ) {
      return;
    }

    // Free users cannot open the profile modal at all — show the upgrade modal
    if (!isProUser) {
      setShowPaywallSheet(true);
      return;
    }

    setShowUserProfileModal(true);
  };

  /**
   * Handle original post user profile click (for reposts) with restriction
   */
  const handleOriginalUserProfileClick = (e) => {
    e.stopPropagation();

    const currentUser = getUser();
    const isOwnPost = currentUser?.id === currentPost?.original_post?.user?.id;

    if (isOwnPost) {
      return;
    }

    // Free users cannot open the profile modal at all — show the upgrade modal
    if (!isProUser) {
      setShowPaywallSheet(true);
      return;
    }

    setShowOriginalUserProfileModal(true);
  };

  /**
   * Handle reaction user profile click with subscription restriction
   */
  const handleReactionUserClick = (reactionUser) => {
    const currentUser = getUser();

    if (reactionUser.id === currentUser?.id) {
      return;
    }

    if (!isProUser) {
      const viewCount = getUserProfileViewCount();
      if (viewCount >= 1) {
        setShowPaywallSheet(true);
        return;
      }
      incrementUserProfileViewCount();
    }

    setSelectedReactionUser(reactionUser);
    setShowReactionUserProfileModal(true);
  };

  // Check if mobile view
  const isMobile = window.innerWidth <= 768;

  // Handle backdrop click - only close on desktop
  const handleBackdropClick = () => {
    if (!isMobile) {
      onClose?.();
    }
  };

  function formatRelativeTime(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    if (isNaN(date)) return "Invalid date";
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    if (diffSec < 60) {
      return "Just now";
    }
    if (diffMin < 60) {
      return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
    }
    if (diffHr < 24) {
      return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
    }
    if (diffDay === 1) {
      return "Yesterday";
    }
    if (diffDay < 7) {
      return `${diffDay} days ago`;
    }
    const diffWeeks = Math.floor(diffDay / 7);
    if (diffDay < 30) {
      return `${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;
    }
    const diffMonths = Math.floor(diffDay / 30);
    if (diffDay < 365) {
      return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
    }
    const diffYears = Math.floor(diffDay / 365);
    return `${diffYears} year${diffYears > 1 ? "s" : ""} ago`;
  }

  // Prevent body scroll on desktop while the modal is open.
  useEffect(() => {
    if (!show) return undefined;
    const mobile = window.innerWidth <= 768;
    if (mobile) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [show]);

  // Close on Escape key
  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  // ❗ AFTER all hooks
  if (!show || !currentPost) return null;

  // Deleted user: post has no user_id and is not anonymous.
  const isDeletedUser =
    !currentPost?.user_id && currentPost?.is_post_anonymously !== 1;

  const isOwnPost = getUser()?.id === currentPost?.user_id;

  const FreeIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24">
      <path d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z" fill="#B9C2CD" />
      <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
      <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
    </svg>
  );
  const ProIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24">
      <path d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z" fill="#0395D3" />
      <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
      <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
    </svg>
  );
  const NewsletterIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24">
      <path d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z" fill="#C8941C" />
      <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
      <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
    </svg>
  );
  const PlusIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24">
      <path d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z" fill="#16A34A" />
      <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
      <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
    </svg>
  );

  const renderPlanIcon = (planName) => {
    if (!planName) return null;
    if (planName === "Free") return <FreeIcon />;
    if (planName === "Plus") return <PlusIcon />;
    if (planName === "Pro" || planName === "Pro Trial") return <ProIcon />;
    if (planName.toLowerCase() === "pro + newsletter") return <NewsletterIcon />;
    return null;
  };

  const reactionIcons = {
    like: (
      <ThumbsUp
        height={22}
        fill={currentPost.user_reaction ? "currentColor" : "none"}
      />
    ),
    love: <img src={loveImg} alt="love" width={24} height={22} />,
    haha: <img src={hahaImg} alt="haha" width={24} height={22} />,
    wow: <img src={wowImg} alt="wow" width={24} height={22} />,
    sad: <img src={sadImg} alt="sad" width={24} height={22} />,
    angry: <img src={angryImg} alt="angry" width={24} height={22} />,
  };

  const hasValidLinkPreview = (preview) => {
    if (!preview) return false;

    let parsed = preview;

    if (typeof preview === "string") {
      try {
        parsed = JSON.parse(preview);
      } catch (err) {
        console.error("Invalid link_preview JSON", err);
        return false;
      }
    }

    return Boolean(
      parsed?.image?.trim() ||
        parsed?.title?.trim() ||
        parsed?.description?.trim() ||
        parsed?.domain?.trim(),
    );
  };

  const getParsedLinkPreview = (preview) => {
    if (!preview) return null;
    if (typeof preview === "string") {
      try {
        return JSON.parse(preview);
      } catch {
        return null;
      }
    }
    return preview;
  };
  const linkPreview = getParsedLinkPreview(currentPost?.link_preview);

  return (
    <div className="community-page-content">
      <div className="article-modal-backdrop" onClick={handleBackdropClick}>
        <div className="article-modal" onClick={(e) => e.stopPropagation()}>
          {/* Close Button */}
          <Button
            variant="link"
            className="icon-btn"
            onClick={() => onClose?.()}
            aria-label="Close"
          >
            <X size={18} />
          </Button>

          <div className="article-modal-content">
            {/* Repost Indicator */}
            {currentPost.is_repost && (
              <div
                style={{
                  padding: "8px 16px",
                  borderBottom: "1px solid var(--mmp-border)",
                  color: "var(--mmp-text-secondary)",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "16px",
                }}
              >
                <Repeat2 size={16} />
                <span>
                  <strong>
                    {(() => {
                      const currentUser = getUser();
                      const isCurrentUser =
                        currentUser?.id === currentPost.user_id;
                      return isCurrentUser ? "You" : currentPost.user_name;
                    })()}
                  </strong>{" "}
                  {(() => {
                    const currentUser = getUser();
                    const isCurrentUser =
                      currentUser?.id === currentPost.user_id;
                    return isCurrentUser ? "have" : "has";
                  })()}{" "}
                  reposted this post
                </span>
              </div>
            )}
            <div className="article-modal-header">
              <div className="author-block">
                <Image
                  src={
                    currentPost.is_post_anonymously === 1
                      ? `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          "Anonymous",
                        )}&background=0195D3&color=fff&size=40`
                      : isDeletedUser
                      ? `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          "Deleted User",
                        )}&background=9CA3AF&color=fff&size=40`
                      : `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${currentPost.user_image}`
                  }
                  alt={
                    currentPost.is_post_anonymously === 1
                      ? "Anonymous"
                      : isDeletedUser
                      ? "Deleted User"
                      : currentPost.user_name
                  }
                  roundedCircle
                  width={40}
                  height={40}
                  style={{
                    cursor:
                      currentPost.is_post_anonymously !== 1 &&
                      !isDeletedUser &&
                      getUser()?.id !== currentPost?.user_id
                        ? "pointer"
                        : "default",
                  }}
                  onClick={handleUserProfileClick}
                  onError={(e) => {
                    e.target.onerror = null;
                    const fallbackName = isDeletedUser
                      ? "Deleted User"
                      : currentPost.user_name || "User";
                    const fallbackBg = isDeletedUser ? "9CA3AF" : "0195D3";
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      fallbackName,
                    )}&background=${fallbackBg}&color=fff&size=40`;
                  }}
                />

                <div className="author-meta">
                  <div
                    className="author-name"
                    style={{
                      cursor:
                        currentPost.is_post_anonymously !== 1 &&
                        !isDeletedUser &&
                        getUser()?.id !== currentPost?.user_id
                          ? "pointer"
                          : "default",
                    }}
                    onClick={handleUserProfileClick}
                  >
                    {currentPost.is_post_anonymously === 1
                      ? "Anonymous"
                      : isDeletedUser
                      ? "Deleted User"
                      : currentPost.user_name}
                    {currentPost.is_post_anonymously !== 1 &&
                    !isDeletedUser &&
                    currentPost?.user_state
                      ? `, ${currentPost.user_state}`
                      : ""}
                    {currentPost.is_post_anonymously !== 1 && !isDeletedUser && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        {renderPlanIcon(
                          currentPost.plan_name || currentPost.user?.plan?.name || planName,
                        )}
                      </span>
                    )}
                  </div>
                  <div className="post-time">
                    {formatRelativeTime(currentPost.created_at)}
                  </div>
                </div>
              </div>

              <div className="header-actions">
                {currentPost.category && (
                  <Badge bg="light" text="dark" className="category-pill">
                    {currentPost.category}
                  </Badge>
                )}
              </div>
            </div>

            {/* Post Title - Only show for non-reposts */}
            {!currentPost?.is_repost && currentPost?.title && (
              <div className="post-title">
                <div className="post-text">{currentPost.title}</div>
              </div>
            )}

            {/* Original Post Display for Reposts */}
            {currentPost.is_repost && currentPost.original_post && (
              <div
                style={{
                  margin: "8px 0 12px",
                  padding: "12px",
                  border: "1px solid var(--mmp-accent-border)",
                  borderRadius: "10px",
                  backgroundColor: "var(--mmp-accent-bg)",
                }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{
                    padding: "0 1px 6px",
                  }}
                >
                  <div className="author-block">
                    <Image
                      src={
                        currentPost.original_post.user?.image
                          ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${currentPost.original_post.user.image}`
                          : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              currentPost.original_post.user?.name || "User",
                            )}&background=0195D3&color=fff&size=42`
                      }
                      roundedCircle
                      width={40}
                      height={40}
                      style={{
                        objectFit: "cover",
                        cursor:
                          getUser()?.id !== currentPost?.original_post?.user?.id
                            ? "pointer"
                            : "default",
                      }}
                      onClick={handleOriginalUserProfileClick}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          currentPost.original_post.user?.name || "User",
                        )}&background=0195D3&color=fff&size=42`;
                      }}
                    />
                    <div className="author-meta">
                      <div
                        className="author-name"
                        style={{
                          cursor:
                            getUser()?.id !==
                            currentPost?.original_post?.user?.id
                              ? "pointer"
                              : "default",
                        }}
                        onClick={handleOriginalUserProfileClick}
                      >
                        {currentPost.original_post.user?.name || "Unknown User"}
                        {currentPost.original_post.user?.state &&
                          `, ${currentPost.original_post.user.state}`}
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          {renderPlanIcon(
                            currentPost.original_post.user?.plan?.name,
                          )}
                        </span>
                      </div>
                      <div className="post-time">
                        {new Date(
                          currentPost.original_post.created_at,
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Original Post Title */}
                {currentPost.original_post.title && (
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "16px",
                      marginBottom: "8px",
                    }}
                  >
                    {currentPost.original_post.title}
                  </div>
                )}

                {/* Original Post Images */}
                {currentPost.original_post.images &&
                  currentPost.original_post.images.length > 0 && (
                    <div
                      style={{
                        marginTop: "12px",
                        marginBottom: "12px",
                        position: "relative",
                      }}
                    >
                      {currentPost.original_post.images.length === 1 ? (
                        <div style={{ position: "relative" }}>
                          <button
                            className="image-zoom-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOriginalZoomImageIndex(0);
                              setShowOriginalImageZoom(true);
                            }}
                            title="Click to zoom"
                            style={{
                              zIndex: 1000,
                              pointerEvents: "auto",
                            }}
                          >
                            <ZoomIn size={20} />
                          </button>
                          <Image
                            key={resolvedTheme}
                            src={`${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${currentPost.original_post.images[0].image_path}`}
                            fluid
                            style={{
                              borderRadius: "8px",
                              maxHeight: "500px",
                              width: "100%",
                              objectFit: "contain",
                              backgroundColor: "var(--mmp-hover-bg)",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                imageNotAvailableSrc;
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ position: "relative" }}>
                          <button
                            className="image-zoom-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOriginalZoomImageIndex(0);
                              setShowOriginalImageZoom(true);
                            }}
                            title="Click to zoom"
                            style={{
                              zIndex: 1000,
                              pointerEvents: "auto",
                            }}
                          >
                            <ZoomIn size={20} />
                          </button>
                          <Carousel
                            slide={false}
                            indicators={false}
                            interval={null}
                            touch={true}
                            className="w-100"
                            style={{ borderRadius: "8px", overflow: "hidden" }}
                            onSelect={(selectedIndex) =>
                              setOriginalZoomImageIndex(selectedIndex)
                            }
                          >
                            {currentPost.original_post.images.map(
                              (img, idx) => (
                                <Carousel.Item key={idx}>
                                  <Image
                                    key={resolvedTheme}
                                    src={`${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`}
                                    style={{
                                      width: "100%",
                                      height: "400px",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src =
                                        imageNotAvailableSrc;
                                    }}
                                  />
                                </Carousel.Item>
                              ),
                            )}
                          </Carousel>
                        </div>
                      )}
                    </div>
                  )}

                {/* Original Post Content */}
                {currentPost.original_post.content && (
                  <>
                    <div
                      style={{
                        fontSize: "14px",
                        whiteSpace: "pre-wrap",
                        display: isOriginalPostExpanded
                          ? "block"
                          : "-webkit-box",
                        WebkitLineClamp: isOriginalPostExpanded ? "unset" : 3,
                        WebkitBoxOrient: "vertical",
                        overflow: isOriginalPostExpanded ? "visible" : "hidden",
                      }}
                    >
                      {(() => {
                        const originalContent = currentPost.original_post.content || "";
                        const originalHasHtml = /<\/?[a-z][\s\S]*>/i.test(originalContent);
                        if (originalHasHtml) {
                          return (
                            <Linkify>
                              <div className="rich-content">
                                {parse(sanitizeRichContent(originalContent))}
                              </div>
                            </Linkify>
                          );
                        }
                        return originalContent.split(/\r?\n/).map((line, lineIndex) => (
                          <div
                            key={lineIndex}
                            style={{ marginTop: lineIndex > 0 ? "4px" : "0" }}
                          >
                            {linkifyText(line).map((part, partIndex) =>
                              part.type === "link" ? (
                                <a
                                  key={partIndex}
                                  href={part.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    color: "var(--mmp-accent)",
                                    textDecoration: "underline",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {part.content}
                                </a>
                              ) : (
                                <span key={partIndex}>{part.content}</span>
                              ),
                            )}
                          </div>
                        ));
                      })()}
                    </div>

                    {/* See more / See less button for original post */}
                    {(() => {
                      const contentText = currentPost.original_post.content;
                      const hasLongContent =
                        getVisibleTextLength(contentText) > 150 ||
                        contentText.split(/\r?\n/).length > 3;

                      return (
                        hasLongContent && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsOriginalPostExpanded(
                                !isOriginalPostExpanded,
                              );
                            }}
                            style={{
                              color: "var(--mmp-accent)",
                              cursor: "pointer",
                              fontWeight: 500,
                              marginTop: "4px",
                              fontSize: "14px",
                            }}
                          >
                            {isOriginalPostExpanded ? "See less" : "See more"}
                          </div>
                        )
                      );
                    })()}
                  </>
                )}
              {/* Original Post Poll */}
              {currentPost.original_post.poll && (
                <div onClick={(e) => e.stopPropagation()}>
                  <PollWidget poll={currentPost.original_post.poll} readOnly />
                </div>
              )}
              </div>
            )}

            {/* Body */}
            <div className="article-modal-body">
              <div
                className="article-content"
                style={
                  // A repost with no added comment has no body content here —
                  // collapse the wrapper's margin/padding so it doesn't leave a
                  // big empty gap between the quoted post and the action bar.
                  currentPost.is_repost && !currentPost.repost_comment
                    ? { margin: 0, padding: 0 }
                    : undefined
                }
              >
                <div
                  style={{
                    display: isExpanded ? "block" : "-webkit-box",
                    WebkitLineClamp: isExpanded ? "unset" : 3,
                    WebkitBoxOrient: "vertical",
                    overflow: isExpanded ? "visible" : "hidden",
                  }}
                >
                  {!currentPost.is_repost && (() => {
                    const hasHtml = typeof currentPost.content === "string" && /<\/?[a-z][\s\S]*>/i.test(currentPost.content);
                    return hasHtml
                      ? <Linkify> <div className="post-text rich-content">{parse(sanitizeRichContent(currentPost.content))}</div> </Linkify>
                      : (currentPost.content || "").split(/\r?\n/).map((line, lineIndex) => {
                          const isSubPoint = /^(\s*[-•]|\s*\d+\.)/.test(line);
                          const isHeading = /:$/.test(line.trim());
                          return (
                            <div key={lineIndex} style={{ marginLeft: isSubPoint ? "20px" : "0px", marginTop: isHeading ? "10px" : isSubPoint ? "4px" : "8px", fontWeight: isHeading ? 600 : "normal" }}>
                              {linkifyText(line).map((part, partIndex) =>
                                part.type === "link"
                                  ? <a key={partIndex} href={part.href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "var(--mmp-accent)", textDecoration: "underline", wordBreak: "break-word" }}>{part.content}</a>
                                  : <span key={partIndex}>{part.content}</span>
                              )}
                            </div>
                          );
                        });
                  })()}
                </div>

                {/* See more / See less button for main post */}
                {!currentPost.is_repost &&
                  (() => {
                    const contentText = currentPost.content || "";
                    const hasLongContent =
                      getVisibleTextLength(contentText) > 150 ||
                      contentText.split(/\r?\n/).length > 3;

                    return (
                      hasLongContent && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                          }}
                          style={{
                            color: "var(--mmp-accent)",
                            cursor: "pointer",
                            fontWeight: 500,
                            marginTop: "8px",
                            fontSize: "14px",
                          }}
                        >
                          {isExpanded ? "See less" : "See more"}
                        </div>
                      )
                    );
                  })()}

                {/* Show repost comment for reposts */}
                {currentPost.is_repost && currentPost.repost_comment && (
                  <div style={{ fontSize: "14px", marginBottom: "16px" }}>
                    {currentPost.repost_comment
                      .split(/\r?\n/)
                      .map((line, lineIndex) => (
                        <div
                          key={lineIndex}
                          style={{ marginTop: lineIndex > 0 ? "4px" : "0" }}
                        >
                          {linkifyText(line).map((part, partIndex) =>
                            part.type === "link" ? (
                              <a
                                key={partIndex}
                                href={part.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  color: "var(--mmp-accent)",
                                  textDecoration: "underline",
                                  wordBreak: "break-word",
                                }}
                              >
                                {part.content}
                              </a>
                            ) : (
                              <span key={partIndex}>{part.content}</span>
                            ),
                          )}
                        </div>
                      ))}
                  </div>
                )}

                {/* Link preview - only for non-reposts */}
                {!currentPost.is_repost && hasValidLinkPreview(linkPreview) && (
                  <div
                    className="link-preview-card mt-2 p-2 border rounded position-relative"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      window.open(contentLink, "_blank", "noopener,noreferrer");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        window.open(
                          contentLink,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }
                    }}
                  >
                    <div className="d-flex gap-2">
                      {linkPreview?.image && (
                        <Image
                          src={linkPreview.image}
                          width={80}
                          height={80}
                          className="rounded"
                          style={{ objectFit: "cover" }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}

                      <div className="flex-grow-1">
                        {linkPreview?.title && (
                          <h6 className="mb-1 text-truncate">
                            {linkPreview.title}
                          </h6>
                        )}

                        {linkPreview?.description && (
                          <p className="mb-1 text-muted small line-clamp-2">
                            {linkPreview.description}
                          </p>
                        )}

                        {linkPreview?.domain && (
                          <small className="text-primary">
                            {linkPreview.domain}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Embedded Poll */}
              {currentPost.poll && (
                <PollWidget poll={currentPost.poll} />
              )}

              {/* Only show main post images for non-reposts */}
              {!currentPost.is_repost &&
                Array.isArray(currentPost?.post_images) &&
                currentPost.post_images.length > 0 &&
                currentPost.post_images[0]?.image_path &&
                (currentPost.post_images.length > 1 ? (
                  <div
                    className="article-image-wrap"
                    style={{ position: "relative" }}
                  >
                    <button
                      className="image-zoom-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomImageIndex(index);
                        setShowImageZoom(true);
                      }}
                      title="Click to zoom"
                    >
                      <ZoomIn size={20} />
                    </button>
                    <Carousel
                      slide={false}
                      activeIndex={index}
                      indicators={false}
                      interval={null}
                      touch={true}
                      onSelect={handleSelect}
                      className="w-100 h-100"
                    >
                      {currentPost.post_images.map((img, imgIndex) => (
                        <Carousel.Item key={imgIndex} className="h-100">
                          <img
                            key={resolvedTheme}
                            src={`${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`}
                            alt={`Post slide ${imgIndex + 1}`}
                            className="d-block w-100 carousel-img"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                imageNotAvailableSrc;
                            }}
                          />
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  </div>
                ) : (
                  <div
                    className="article-image-wrap"
                    style={{ position: "relative" }}
                  >
                    <button
                      className="image-zoom-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomImageIndex(0);
                        setShowImageZoom(true);
                      }}
                      title="Click to zoom"
                    >
                      <ZoomIn size={20} />
                    </button>
                    <img
                      key={resolvedTheme}
                      src={`${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${currentPost.post_images[0]?.image_path}`}
                      alt="Post "
                      className="d-block w-100 carousel-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          imageNotAvailableSrc;
                      }}
                    />
                  </div>
                ))}

              {/* Reaction Summary */}
              <div className="py-2 border-bottom">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center reaction-container">
                    <div className="d-flex align-items-center reaction-container">
                      {(() => {
                        const totalReactions = Object.values(
                          currentPost.reactions || {},
                        ).reduce((sum, count) => sum + count, 0);
                        const currentReactionTotal = totalReactions;
                        const legacyLikesCount = Math.max(
                          0,
                          currentPost?.likes_count - currentReactionTotal,
                        );
                        const allReactionsCount =
                          totalReactions + legacyLikesCount;

                        if (allReactionsCount === 0) {
                          return null;
                        }

                        const hasUserReaction = currentPost.user_reaction;

                        if (allReactionsCount === 1 && hasUserReaction) {
                          const reactionEmojis = {
                            like: (
                              <ThumbsUp
                                height={22}
                                fill={
                                  currentPost.user_reaction
                                    ? "currentColor"
                                    : "none"
                                }
                              />
                            ),
                            love: (
                              <img
                                src={loveImg}
                                alt="love"
                                width={24}
                                height={22}
                              />
                            ),
                            haha: (
                              <img
                                src={hahaImg}
                                alt="haha"
                                width={24}
                                height={22}
                              />
                            ),
                            wow: (
                              <img
                                src={wowImg}
                                alt="wow"
                                width={24}
                                height={22}
                              />
                            ),
                            sad: (
                              <img
                                src={sadImg}
                                alt="sad"
                                width={24}
                                height={22}
                              />
                            ),
                            angry: (
                              <img
                                src={angryImg}
                                alt="angry"
                                width={24}
                                height={22}
                              />
                            ),
                          };

                          return (
                            <div className="d-flex align-items-center gap-1">
                              <div
                                className={`d-flex align-items-center gap-2 action-btn ${
                                  currentPost.user_reaction
                                    ? "text-primary"
                                    : "text-muted"
                                }`}
                              >
                                {" "}
                                {reactionEmojis[hasUserReaction]}
                              </div>
                              <span style={{ fontSize: "14px" }}>You</span>
                            </div>
                          );
                        }

                        return (
                          <div
                            onClick={() => setShowReactionsModal(true)}
                            style={{ cursor: "pointer" }}
                          >
                            <FacebookCounter
                              counters={(() => {
                                const order = [
                                  "like",
                                  "love",
                                  "haha",
                                  "wow",
                                  "sad",
                                  "angry",
                                ];
                                const list = [];
                                const counts = currentPost.reactions || {};

                                const reactionTotal = Object.values(
                                  counts,
                                ).reduce((sum, count) => sum + count, 0);
                                const legacyCount = Math.max(
                                  0,
                                  currentPost.likes_count - reactionTotal,
                                );

                                for (let i = 0; i < legacyCount; i++) {
                                  list.push({
                                    emoji: "like",
                                    by: `legacy-like-${i}`,
                                  });
                                }

                                order.forEach((type) => {
                                  const n = Math.max(0, counts[type] || 0);
                                  for (let i = 0; i < n; i++) {
                                    if (
                                      currentPost.user_reaction === type &&
                                      !list.some(
                                        (e) =>
                                          e.by === "You" && e.emoji === type,
                                      )
                                    ) {
                                      list.push({ emoji: type, by: "You" });
                                    } else {
                                      list.push({
                                        emoji: type,
                                        by: `user-${type}-${i}`,
                                      });
                                    }
                                  }
                                });
                                return list;
                              })()}
                              user={"You"}
                              bg={"var(--mmp-surface)"}
                              alwaysShowOthers={true}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="d-flex align-items-center comments-counter">
                    {currentPost.comments_count > 0 && (
                      <span>{currentPost.comments_count} comments</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Post Actions */}
              <div className="d-flex align-items-center justify-content-between py-2">
                <div
                  className="d-flex align-items-center gap-3"
                  style={{ fontSize: "14px", fontWeight: 500 }}
                >
                  {/* Like Button */}
                  <div
                    className="position-relative"
                    onMouseEnter={handleReactionsEnter}
                    onMouseLeave={handleReactionsLeave}
                  >
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleLike}
                      className={`p-0 d-flex align-items-center gap-2 action-btn ${
                        currentPost?.user_reaction
                          ? "text-primary"
                          : "text-muted"
                      }`}
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        textDecoration: "none",
                      }}
                    >
                      <span
                        style={{
                          height: "22px",
                          fontSize: "22px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {currentPost.user_reaction ? (
                          reactionIcons[currentPost.user_reaction] || (
                            <ThumbsUp size={22} />
                          )
                        ) : (
                          <ThumbsUp size={22} />
                        )}
                      </span>
                      {Object.values(currentPost.reactions).reduce(
                        (sum, count) => sum + count,
                        0,
                      ) > 0 && (
                        <span>
                          {Object.values(currentPost.reactions).reduce(
                            (sum, count) => sum + count,
                            0,
                          )}
                        </span>
                      )}
                    </Button>
                    {showReactionPicker && (
                      <div
                        className="reaction-picker-popup"
                        style={{
                          position: "absolute",
                          bottom: "100%",
                          left: 16,
                          zIndex: 1000,
                          marginBottom: 8,
                        }}
                        onMouseEnter={handleReactionsEnter}
                        onMouseLeave={handleReactionsLeave}
                      >
                        <FacebookSelector onSelect={handleReactionPick} />
                      </div>
                    )}
                  </div>

                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 d-flex align-items-center gap-2 action-btn text-muted"
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    <MessageSquare size={22} strokeWidth={2} />
                    <span>Comment</span>
                  </Button>

                  {/* Share — opens the shared share modal via the feed handler */}
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleShareClick}
                    className="p-0 d-flex align-items-center gap-2 action-btn text-muted"
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ width: 22, height: 22 }}
                    >
                      <path d="m22 2-7 20-4-9-9-4 20-7z" />
                    </svg>
                    <span>Share</span>
                  </Button>
                </div>

                {/* Right group: Save + Info — matches the feed's grouping */}
                <div
                  className="d-flex align-items-center gap-3"
                  style={{ fontSize: "14px", fontWeight: 500 }}
                >
                  {/* Save / Bookmark — mirrors PostCard.js */}
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleSaveClick}
                    disabled={saveLoading}
                    className={`p-0 d-flex align-items-center gap-2 action-btn ${
                      isSaved ? "text-primary" : "text-muted"
                    }`}
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    <Bookmark
                      size={22}
                      fill={isSaved ? "currentColor" : "none"}
                    />
                    <span>{isSaved ? "Saved" : "Save"}</span>
                  </Button>

                  {/* Info / Report — opens the reasons modal */}
                  {!isOwnPost && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setShowReportModal(true)}
                      aria-label="Report"
                      className="p-0 d-flex align-items-center action-btn text-muted"
                      style={{ textDecoration: "none" }}
                    >
                      <Info size={22} />
                    </Button>
                  )}
                </div>
              </div>
              {/* Comments Section */}
              <CommentsSection
                postId={currentPost.id}
                onCommentCountUpdate={onCommentCountUpdate}
              />

              {/* Image Zoom Modal */}
              <ImageZoomModal
                show={showImageZoom}
                onClose={() => setShowImageZoom(false)}
                images={currentPost.post_images?.map(
                  (img) =>
                    `${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`,
                )}
                currentIndex={zoomImageIndex}
              />

              {/* Original Post Image Zoom Modal */}
              {currentPost.is_repost && currentPost.original_post?.images && (
                <ImageZoomModal
                  show={showOriginalImageZoom}
                  onClose={() => setShowOriginalImageZoom(false)}
                  images={currentPost.original_post.images.map(
                    (img) =>
                      `${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`,
                  )}
                  currentIndex={originalZoomImageIndex}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Profile Modal - Main Post */}
      <UserProfileModal
        show={showUserProfileModal}
        onHide={() => setShowUserProfileModal(false)}
        userId={currentPost?.user_id}
        userName={currentPost?.user_name}
        userImage={currentPost?.user_image}
        userState={currentPost?.user_state}
      />

      {/* User Profile Modal - Original Post */}
      <UserProfileModal
        show={showOriginalUserProfileModal}
        onHide={() => setShowOriginalUserProfileModal(false)}
        userId={currentPost?.original_post?.user?.id}
        userName={currentPost?.original_post?.user?.name}
        userImage={currentPost?.original_post?.user?.image}
        userState={currentPost?.original_post?.user?.state}
      />

      {/* Reaction User Profile Modal */}
      <UserProfileModal
        show={showReactionUserProfileModal}
        onHide={() => {
          setShowReactionUserProfileModal(false);
          setSelectedReactionUser(null);
        }}
        userId={selectedReactionUser?.id}
        userName={selectedReactionUser?.user_name}
        userImage={selectedReactionUser?.profile_photo_path}
        userState={selectedReactionUser?.state}
      />

      {/* Follow / profile restriction modal for non-pro users */}
      <RestrictionModal
        isVisible={showPaywallSheet}
        onClose={() => setShowPaywallSheet(false)}
        eventSource="follow_limit"
        iconType="user-follow"
        heading="Want to follow or chat with this user?"
        subText="Upgrade to connect and build real relationships."
        infoText="Pro users can follow, chat, and connect with anyone in the community."
      />

      {/* Save / bookmark restriction modal for non-pro users */}
      <RestrictionModal
        isVisible={showSaveRestriction}
        onClose={() => setShowSaveRestriction(false)}
        eventSource="save_limit"
        iconType="bookmark"
        heading="Want to save this post?"
        subText="Upgrade to Pro to bookmark posts and find them later."
        infoText="Pro users can save unlimited community posts to revisit anytime."
      />

      {/* Fallback share modal — only mounted when there's no parent onShare
        handler (e.g. the standalone /community/:id route). */}
      <ShareModal
        show={showShareModal}
        post={currentPost}
        onClose={() => setShowShareModal(false)}
      />

      {/* Report Post Modal — markup matches PostCard / RightSidebar so it
        inherits the same global styles (custom-modal, radio-options, etc.). */}
      <Modal
        show={showReportModal}
        onHide={closeReportModal}
        centered
        size="lg"
        dialogClassName="custom-modal profile-other-details-modal"
      >
        <Modal.Header>
          <h5 className="modal-title">Report Post</h5>
          <Button className="close-btn" onClick={closeReportModal}>
            <FiX size={24} />
          </Button>
        </Modal.Header>

        <Modal.Body className="pt-0">
          <div className="radio-options">
            <div className="row">
              {REPORT_REASONS.map((reason) => (
                <div
                  key={reason}
                  className={`radio-option col-12 col-md-6 ${
                    reportReason === reason ? "selected" : ""
                  }`}
                  onClick={() => setReportReason(reason)}
                >
                  <div className="contentwrap">
                    <input
                      type="radio"
                      name="reportReason"
                      id={`detail-${reason}`}
                      value={reason}
                      checked={reportReason === reason}
                      readOnly
                    />
                    <label htmlFor={`detail-${reason}`} className="radio-label">
                      {reason}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="buttons text-center mt-2">
            <Button
              variant="primary"
              onClick={submitReport}
              disabled={!reportReason}
            >
              Submit <ChevronRightIcon className="button-icon" />
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Reactions Modal */}
      <Modal
        show={showReactionsModal}
        onHide={() => setShowReactionsModal(false)}
        centered
        size="md"
        dialogClassName="custom-modal"
      >
        <Modal.Header
          style={{
            padding: "20px 24px 10px",
            border: "none",
          }}
        >
          <h5
            className="modal-title"
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "var(--mmp-text-primary)",
              margin: 0,
            }}
          >
            Reactions
          </h5>
          <Button
            className="close-btn"
            onClick={() => setShowReactionsModal(false)}
            variant="link"
            style={{
              background: "none",
              border: "none",
              padding: "0",
              color: "var(--mmp-text-secondary)",
            }}
          >
            <FiX size={24} />
          </Button>
        </Modal.Header>

        <Modal.Body
          style={{
            padding: "10px 24px 24px",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {(() => {
            const likedUsers = currentPost?.liked_users || [];

            if (likedUsers && likedUsers.length > 0) {
              return (
                <div className="reactions-list">
                  {likedUsers.map((likedUser, likedIndex) => (
                    <div
                      key={`${likedUser.id}-${likedIndex}`}
                      className="d-flex align-items-center justify-content-between"
                      style={{
                        transition: "background-color 0.2s ease",
                        borderRadius: "8px",
                        margin: "0 -8px",
                        padding: "12px 8px",
                        borderBottom:
                          likedIndex < likedUsers.length - 1
                            ? "1px solid var(--mmp-border)"
                            : "none",
                        cursor:
                          likedUser.id !== getUser()?.id
                            ? "pointer"
                            : "default",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                    >
                      <div
                        className="d-flex align-items-center"
                        onClick={() => handleReactionUserClick(likedUser)}
                        style={{
                          cursor:
                            likedUser.id !== getUser()?.id
                              ? "pointer"
                              : "default",
                          flex: 1,
                        }}
                      >
                        <Image
                          src={
                            likedUser.profile_photo_path
                              ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${likedUser.profile_photo_path}`
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  likedUser.user_name || "User",
                                )}&background=0195D3&color=fff&size=40`
                          }
                          alt={likedUser.user_name || "User"}
                          roundedCircle
                          width={40}
                          height={40}
                          className="me-3"
                          style={{
                            objectFit: "cover",
                            cursor:
                              likedUser.id !== getUser()?.id
                                ? "pointer"
                                : "default",
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              likedUser.user_name || "User",
                            )}&background=0195D3&color=fff&size=40`;
                          }}
                        />
                        <div>
                          <div
                            className="fw-medium"
                            style={{
                              fontSize: "14px",
                              color: "var(--mmp-text-primary)",
                              cursor:
                                likedUser.id !== getUser()?.id
                                  ? "pointer"
                                  : "default",
                              transition: "color 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (likedUser.id !== getUser()?.id) {
                                e.target.style.color = "var(--mmp-accent)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.color = "var(--mmp-text-primary)";
                            }}
                          >
                            {likedUser.user_name || "Unknown User"}
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "12px" }}
                          >
                            {likedUser.reacted_at
                              ? new Date(
                                  likedUser.reacted_at,
                                ).toLocaleDateString()
                              : ""}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center">
                        {likedUser.reaction_type && (
                          <div
                            className="d-flex align-items-center gap-1"
                            title={
                              likedUser.reaction_type.charAt(0).toUpperCase() +
                              likedUser.reaction_type.slice(1)
                            }
                          >
                            {likedUser.reaction_type === "like" && (
                              <ThumbsUp
                                height={20}
                                fill="currentColor"
                                style={{ color: "#1877f2" }}
                              />
                            )}
                            {likedUser.reaction_type === "love" && (
                              <img
                                src={loveImg}
                                alt="love"
                                width={20}
                                height={20}
                              />
                            )}
                            {likedUser.reaction_type === "haha" && (
                              <img
                                src={hahaImg}
                                alt="haha"
                                width={20}
                                height={20}
                              />
                            )}
                            {likedUser.reaction_type === "wow" && (
                              <img
                                src={wowImg}
                                alt="wow"
                                width={20}
                                height={20}
                              />
                            )}
                            {likedUser.reaction_type === "sad" && (
                              <img
                                src={sadImg}
                                alt="sad"
                                width={20}
                                height={20}
                              />
                            )}
                            {likedUser.reaction_type === "angry" && (
                              <img
                                src={angryImg}
                                alt="angry"
                                width={20}
                                height={20}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            } else {
              return (
                <div className="text-center py-4 text-muted">
                  <p>No reactions yet</p>
                </div>
              );
            }
          })()}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PostDetailModal;
